import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import type { AgGridReact as AgGridReactType } from "ag-grid-react";
import type {
  ColDef,
  CellValueChangedEvent,
  FilterChangedEvent,
  GridReadyEvent,
  SortChangedEvent,
  CellEditingStartedEvent,
  RowClickedEvent,
  SelectionChangedEvent,
} from "ag-grid-community";
import OptionFilter, {
  type Option,
  type FilterMode,
  type OptionFilterModel,
} from "./filters/OptionFilter";
import PaginationBar from "./PaginationBar";
import { TrashBinIcon } from "assets/icons";
import SnackbarUtils from "utils/SnackbarUtils";
import { Pen, SaveAll, Undo } from "lucide-react";

type ActionHandler<T = any> = (row: T, meta?: { pinned?: boolean }) => void;

type ColumnConfig = ColDef;

type QueryState = {
  filterModel: any;
  sortModel: any;
  pagination: {
    /** Zero-based page index used for data requests */
    pageIndex: number;
    /** One-based page number for APIs/UI */
    page: number;
    pageSize: number;
    totalPages?: number;
    isLastPageFound?: boolean;
  };
};

type ColumnFilterConfig = {
  type: FilterMode; // dropdown or checkbox
  options: Option[];
  onChange?: AgGridTableProps["onQueryChange"];
};

type AgGridTableProps<T = any> = {
  rows: T[];
  columns: ColumnConfig[];
  height?: number | string;
  autoHeight?: boolean;
  pinnedLeftKeys?: string[];
  actionHeaderName?: string;
  onEdit?: ActionHandler<T>;
  onDelete?: ActionHandler<T>;
  onSave?: ActionHandler<T>;
  filtersConfig?: Record<string, ColumnFilterConfig>;
  onFilterChanged?: (event: FilterChangedEvent) => void;
  onSortChanged?: (event: SortChangedEvent) => void;
  onGridReady?: (event: GridReadyEvent) => void;
  onCellValueChanged?: (event: CellValueChangedEvent<T>) => void;
  getRowId?: (params: any) => string;
  serverMode?: boolean;
  onQueryChange?: (query: QueryState) => void;
  stopEditingWhenCellsLoseFocus?: boolean;
  suppressClickEdit?: boolean;
  defaultColDef?: ColDef;
  pagination?: boolean;
  pageSize?: number;
  totalPages?: number;
  onAfterActions?: () => void;
  onRowClicked?: (event: RowClickedEvent<T>) => void;
  onSelectionChanged?: (event: SelectionChangedEvent<T>) => void;
  /**
   * Optional pinned row right under the header for inline data entry.
   * Keep the provided object reference stable while editing to avoid resets.
   */
  showAddRow?: "add" | "edit";
  addRowData?: T;
};

const AgGridTable = <T,>({
  rows = [] as T[],
  columns,
  height = 520,
  autoHeight = true,
  pinnedLeftKeys = [],
  actionHeaderName = "Hành động",
  onEdit,
  onDelete,
  filtersConfig,
  onSave,
  onQueryChange,
  onGridReady,
  onSortChanged,
  onFilterChanged,
  serverMode = true,
  onCellValueChanged,
  getRowId,
  onAfterActions,
  stopEditingWhenCellsLoseFocus,
  suppressClickEdit,
  defaultColDef: defaultColDefProp,
  pagination = true,
  pageSize = 20,
  totalPages: totalPagesProp,
  showAddRow,
  addRowData,
}: AgGridTableProps<T>) => {
  const gridRef = useRef<AgGridReactType<T>>(null);
  const showActions = Boolean(onEdit || onDelete || onSave);
  const [paginationInfo, setPaginationInfo] = useState(() => ({
    pageIndex: 0,
    page: 1,
    pageSize,
    totalPages: totalPagesProp as number | undefined,
    isLastPageFound: totalPagesProp !== undefined ? true : undefined,
  }));

  const [dirtyRowIds, setDirtyRowIds] = useState<Set<string>>(() => new Set());
  const dirtyRowIdsRef = useRef<Set<string>>(new Set());
  const trackingEnabled = Boolean(onSave);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const pageSizeOptions = useMemo<number[]>(() => {
    const base = [100, 500, 1000];
    if (!base.includes(pageSize)) {
      return [pageSize, ...base].sort((a, b) => a - b);
    }
    return base;
  }, [pageSize]);
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      editable: (params) => {
        return (
          params.data?.id === editingRowId || params.node.rowPinned === "top"
        );
      },
      minWidth: 140,
      ...(defaultColDefProp || {}),
    }),
    [defaultColDefProp, editingRowId],
  );

  useEffect(() => {
    showAddRow !== "edit" && setEditingRowId(null);
  }, [showAddRow]);

  useEffect(() => {
    if (totalPagesProp === undefined) return;
    setPaginationInfo((prev) => {
      const clampedPageIndex = Math.min(
        prev.pageIndex,
        Math.max(totalPagesProp - 1, 0),
      );
      const next = {
        ...prev,
        pageIndex: clampedPageIndex,
        page: clampedPageIndex + 1,
        totalPages: totalPagesProp,
        isLastPageFound: true,
      };
      const unchanged =
        prev.pageIndex === next.pageIndex &&
        prev.page === next.page &&
        prev.totalPages === next.totalPages &&
        prev.isLastPageFound === next.isLastPageFound;
      return unchanged ? prev : next;
    });
  }, [totalPagesProp]);

  const buildQueryState = useCallback(
    (paginationOverride?: Partial<QueryState["pagination"]>): QueryState => {
      const api = gridRef.current?.api;
      const nextPagination = {
        pageIndex:
          paginationOverride?.pageIndex ?? paginationInfo.pageIndex ?? 0,
        page:
          paginationOverride?.page ??
          (paginationOverride?.pageIndex ?? paginationInfo.pageIndex ?? 0) + 1,
        pageSize:
          paginationOverride?.pageSize ??
          paginationInfo.pageSize ??
          pageSize,
        totalPages:
          paginationOverride?.totalPages ??
          totalPagesProp ??
          paginationInfo.totalPages,
        isLastPageFound:
          paginationOverride?.isLastPageFound ??
          (totalPagesProp !== undefined
            ? true
            : paginationInfo.isLastPageFound),
      };

      return {
        filterModel: api?.getFilterModel() ?? {},
        sortModel: api?.getState().sort?.sortModel ?? [],
        pagination: nextPagination,
      };
    },
    [
      pageSize,
      paginationInfo.isLastPageFound,
      paginationInfo.pageIndex,
      paginationInfo.pageSize,
      paginationInfo.totalPages,
      totalPagesProp,
    ],
  );

  const resolveRowId = useCallback(
    (
      data: T,
      nodeId?: string,
      rowIndex?: number,
      pinned?: string | null,
    ): string | undefined => {
      if (pinned) {
        return `pinned-${pinned}-${rowIndex ?? ""}`;
      }
      if (getRowId) {
        try {
          const value = getRowId({ data, rowIndex } as any);
          if (value) return value;
        } catch {
          // fallback when getRowId throws
        }
      }
      const dataId = (data as any)?.id;
      if (dataId !== undefined && dataId !== null) {
        return String(dataId);
      }
      if (nodeId) return String(nodeId);
      if (rowIndex !== undefined && rowIndex !== null) return String(rowIndex);
      return undefined;
    },
    [getRowId],
  );

  const markRowDirty = useCallback(
    (rowId?: string) => {
      if (!rowId || !trackingEnabled) return;
      setDirtyRowIds((prev) => {
        if (prev.has(rowId)) return prev;
        const next = new Set(prev);
        next.add(rowId);
        return next;
      });
    },
    [trackingEnabled],
  );

  const clearRowDirty = useCallback((rowId?: string) => {
    if (!rowId) return;
    setDirtyRowIds((prev) => {
      if (!prev.has(rowId)) return prev;
      const next = new Set(prev);
      next.delete(rowId);
      return next;
    });
    setEditingRowId(null);
  }, []);

  useEffect(() => {
    if (trackingEnabled) return;
    setDirtyRowIds(new Set());
  }, [trackingEnabled]);

  useEffect(() => {
    dirtyRowIdsRef.current = dirtyRowIds;
    if (!gridRef.current?.api) return;
    gridRef.current.api.refreshCells({
      columns: ["__actions"],
      force: true,
    });
  }, [dirtyRowIds]);

  useEffect(() => {
    if (!dirtyRowIds.size) return;
    setDirtyRowIds((prev) => {
      if (!prev.size) return prev;
      const existingIds = new Set<string>();
      rows.forEach((row, index) => {
        const id = resolveRowId(row, undefined, index);
        if (id) existingIds.add(id);
      });
      const next = new Set(
        Array.from(prev).filter((id) => existingIds.has(id)),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [rows, resolveRowId, dirtyRowIds]);

  const columnDefs = useMemo<ColDef[]>(() => {
    const withPinned = columns.map((col) => {
      const key = col.field || col.colId;
      if (!key) return col;

      const next: ColDef = {
        ...col,
        ...(pinnedLeftKeys.includes(key) ? { pinned: "left" as const } : {}),
      };

      const filterConfig = filtersConfig ? filtersConfig[String(key)] : null;
      if (!filterConfig) return next;

      const onValueChange = (value: string | string[] | null) => {
        const api = gridRef.current?.api;
        if (!api) return;

        const state = buildQueryState();
        const filterValues = Array.isArray(value)
          ? value.filter(Boolean)
          : value
            ? [value]
            : [];
        const nextFilterModel: OptionFilterModel | undefined =
          filterValues.length
            ? { filterType: "optionFilter", values: filterValues }
            : undefined;

        const mergedValue = {
          ...state,
          filterModel: { ...state.filterModel },
        };
        if (nextFilterModel) {
          mergedValue.filterModel[key as string] = nextFilterModel;
        } else {
          delete mergedValue.filterModel[key as string];
        }
        filterConfig.onChange?.(mergedValue);
      };

      return {
        ...next,
        filter: OptionFilter as any,
        filterParams: {
          ...(next.filterParams || {}),
          options: filterConfig.options,
          mode: filterConfig.type,
          onValueChange,
        },
      };
    });

    const actionCol: ColDef | null = showActions
      ? {
          headerName: actionHeaderName,
          field: "__actions",
          pinned: "right",
          filter: false,
          floatingFilter: false,
          sortable: false,
          editable: false,
          menuTabs: [],
          width: 170,
          minWidth: 130,
          cellRenderer: (params) => {
            if (params.node?.rowPinned && !onSave) return null;
            const data = params.data as T;
            const pinned = !!params.node?.rowPinned;
            const stopEdit = () => params.api?.stopEditing(false);
            const rowId = resolveRowId(
              data,
              params.node?.id,
              params.rowIndex,
              params.node?.rowPinned,
            );

            return (
              <div className="flex  gap-2 h-full con-center justify-center">
                {(pinned || params.data?.id === editingRowId) && (
                  <button
                    className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 text-xs font-semibold"
                    title="Hủy"
                    onClick={() => {
                      // clearRowDirty(null);
                      stopEdit();
                      onAfterActions?.();
                      setEditingRowId(null);
                    }}
                  >
                    <Undo className="w-4 h-4" />
                  </button>
                )}
                {(pinned || params.data?.id === editingRowId) && (
                  <button
                    className="inline-flex items-center gap-1 text-green-600 hover:text-green-700 text-xs font-semibold"
                    title="Lưu"
                    onClick={() => {
                      stopEdit();
                      onSave?.(data, { pinned });
                      clearRowDirty(rowId);
                    }}
                  >
                    <SaveAll className="w-4 h-4" />
                  </button>
                )}

                {!pinned && params.data?.id !== editingRowId && (
                  <button
                    className="text-blue-600 hover:text-blue-700 text-xs font-semibold"
                    onClick={() => {
                      onEdit(params);
                      setEditingRowId(params.data?.id);
                    }}
                  >
                    <Pen className="w-3 h-3" />
                  </button>
                )}

                {onDelete && !pinned && params.data?.id !== editingRowId && (
                  <button
                    className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs font-semibold"
                    onClick={() => {
                      onDelete(data);
                      clearRowDirty(rowId);
                    }}
                  >
                    <TrashBinIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          },
        }
      : null;

    return actionCol ? [...withPinned, actionCol] : withPinned;
  }, [
    columns,
    pinnedLeftKeys,
    filtersConfig,
    buildQueryState,
    actionHeaderName,
    onEdit,
    onDelete,
    onSave,
    showActions,
    resolveRowId,
    clearRowDirty,
  ]);

  const emitQueryChange = useCallback(
    (paginationOverride?: Partial<QueryState["pagination"]>) => {
      if (!serverMode || !onQueryChange) return;
      const state = buildQueryState(paginationOverride);
      onQueryChange(state);
    },
    [buildQueryState, onQueryChange, serverMode],
  );

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      onGridReady?.(event);
    },
    [onGridReady],
  );

  const applyPagination = useCallback(
    (nextPageIndex: number, nextPageSize?: number) => {
      setPaginationInfo((prev) => {
        const total = totalPagesProp ?? prev.totalPages;
        const pageSizeValue = nextPageSize ?? prev.pageSize ?? pageSize;
        const clampedIndex =
          total !== undefined
            ? Math.min(Math.max(0, nextPageIndex), Math.max(total - 1, 0))
            : Math.max(0, nextPageIndex);

        const next: Required<QueryState["pagination"]> = {
          pageIndex: clampedIndex,
          page: clampedIndex + 1,
          pageSize: pageSizeValue,
          totalPages: total,
          isLastPageFound: totalPagesProp !== undefined
            ? true
            : prev.isLastPageFound,
        };

        const unchanged =
          prev.pageIndex === next.pageIndex &&
          prev.page === next.page &&
          prev.pageSize === next.pageSize &&
          prev.totalPages === next.totalPages &&
          prev.isLastPageFound === next.isLastPageFound;

        if (!unchanged) {
          emitQueryChange(next);
        }
        return unchanged ? prev : next;
      });
    },
    [emitQueryChange, pageSize, totalPagesProp],
  );

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      onFilterChanged?.(event);
      applyPagination(0);
    },
    [applyPagination, onFilterChanged],
  );

  const handleSortChanged = useCallback(
    (event: SortChangedEvent) => {
      onSortChanged?.(event);
      emitQueryChange();
    },
    [emitQueryChange, onSortChanged],
  );

  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<T>) => {
      const rowId = resolveRowId(
        event.data,
        event.node?.id,
        event.rowIndex,
        event.node?.rowPinned,
      );
      markRowDirty(rowId);
      onCellValueChanged?.(event);
    },
    [markRowDirty, onCellValueChanged, resolveRowId],
  );

  const handleCellEditingStarted = useCallback(
    (event: CellEditingStartedEvent<T>) => {
      if (!trackingEnabled) return;
      if (!dirtyRowIds.size) return;
      const rowId = resolveRowId(
        event.data,
        event.node?.id,
        event.rowIndex,
        event.node?.rowPinned,
      );
      if (rowId && dirtyRowIds.has(rowId)) return;
      SnackbarUtils.warning(
        "Vui lòng lưu dòng đang chỉnh sửa trước khi thêm/chỉnh sửa dòng khác.",
      );
      event.api.stopEditing(true);
    },
    [dirtyRowIds, resolveRowId, trackingEnabled],
  );

  const handleGoToFirst = useCallback(() => {
    applyPagination(0);
  }, [applyPagination]);

  const handleGoToLast = useCallback(() => {
    const total = totalPagesProp ?? paginationInfo.totalPages;
    if (total !== undefined) {
      applyPagination(Math.max(total - 1, 0));
    } else {
      applyPagination(paginationInfo.pageIndex + 1);
    }
  }, [applyPagination, paginationInfo.pageIndex, paginationInfo.totalPages, totalPagesProp]);

  const handleGoToPrevious = useCallback(() => {
    applyPagination(Math.max(0, paginationInfo.pageIndex - 1));
  }, [applyPagination, paginationInfo.pageIndex]);

  const handleGoToNext = useCallback(() => {
    applyPagination(paginationInfo.pageIndex + 1);
  }, [applyPagination, paginationInfo.pageIndex]);

  const handleGoToPage = useCallback(
    (nextPage: number) => {
      const targetIndex = Math.max(0, Math.floor(nextPage) - 1);
      applyPagination(targetIndex);
    },
    [applyPagination],
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      applyPagination(0, size);
    },
    [applyPagination],
  );

  const pinnedTopRowData = useMemo<T[] | undefined>(() => {
    if (!showAddRow || showAddRow === "edit") return undefined;
    if (!addRowData) return [{} as T];
    return [{ ...addRowData, editable: true }];
  }, [showAddRow, addRowData]);

  const disablePrev = paginationInfo.pageIndex <= 0;
  const effectiveTotalPages = totalPagesProp ?? paginationInfo.totalPages;
  const disableNext =
    effectiveTotalPages !== undefined
      ? paginationInfo.page >= (effectiveTotalPages || 1)
      : false;
  const disableLast =
    effectiveTotalPages !== undefined
      ? paginationInfo.page >= (effectiveTotalPages || 1)
      : paginationInfo.isLastPageFound !== true;
  const currentPageDisplay = paginationInfo.page ?? 1;

  return (
    <div className="w-full space-y-3">
      {pagination && (
        <PaginationBar
          page={currentPageDisplay}
          pageSize={paginationInfo.pageSize}
          totalPages={effectiveTotalPages}
          pageSizeOptions={pageSizeOptions}
          onFirst={handleGoToFirst}
          onPrev={handleGoToPrevious}
          onNext={handleGoToNext}
          onLast={handleGoToLast}
          onGoToPage={handleGoToPage}
          onPageSizeChange={handlePageSizeChange}
          disablePrev={disablePrev}
          disableNext={disableNext}
          disableLast={disableLast}
        />
      )}

      <div
        className="ag-theme-alpine w-full"
        style={autoHeight ? undefined : { height }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout={autoHeight ? "autoHeight" : "normal"}
          animateRows
          onGridReady={handleGridReady}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          onCellValueChanged={handleCellValueChanged}
          onCellEditingStarted={handleCellEditingStarted}
          getRowId={getRowId}
          stopEditingWhenCellsLoseFocus={stopEditingWhenCellsLoseFocus}
          suppressClickEdit={suppressClickEdit}
          enableFilterHandlers={false}
          pinnedTopRowData={pinnedTopRowData}
          pagination={false}
          suppressScrollOnNewData={true}
        />
      </div>
    </div>
  );
};

export type {
  AgGridTableProps,
  ColumnConfig,
  ColumnFilterConfig,
  Option,
  QueryState,
};
export default AgGridTable;
